import React, { useEffect, useState, Suspense } from 'react';

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { OnlineStatusProvider } from './contexts/OnlineStatusContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { queryClient } from './config/queryClient';
import { hardDisableServiceWorker, registerServiceWorker, shouldBypassServiceWorkerForPath } from './utils/serviceWorker';
import Navbar from './components/Navbar';
import { CallRoot } from './components/phone/CallRoot';
import Feed from './pages/Feed';
import Landing from './pages/Landing';
import Catalog from './pages/Catalog';
import CategoryLanding from './pages/CategoryLanding';
import TagsLanding from './pages/TagsLanding';
import Login from './pages/Login';
import Register from './pages/Register';
import EmailVerification from './pages/EmailVerification';
import EmailVerificationSuccess from './pages/EmailVerificationSuccess';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/profile';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import PostDetail from './pages/PostDetail';
import LegacyUserRedirect from './pages/LegacyUserRedirect';
import Search from './pages/Search';
import TagSearch from './pages/TagSearch';
import StaticPage from './pages/StaticPage';
import NotFound from './pages/NotFound';
import HelpPage from './pages/HelpPage';
import HelpHome from './pages/HelpHome';
import HelpSection from './pages/HelpSection';
import CookieBanner from './components/CookieBanner';
import Footer from './components/Footer';
import './styles/index.css';
import AudioUnlocker from './components/AudioUnlocker';
import ScrollToTop from './components/ScrollToTop';

const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Account = React.lazy(() => import('./pages/Account'));
const Tariffs = React.lazy(() => import('./pages/Tariffs'));
const MyEvents = React.lazy(() => import('./pages/MyEvents'));
const MyPosts = React.lazy(() => import('./pages/MyPosts'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Messages = React.lazy(() => import('./pages/Messages'));

// Suppress noisy client-side logs in non-development environments.
// Keep console.error and console.warn so important failures are still visible.
if (process.env.NODE_ENV !== 'development') {
  ['log', 'debug', 'info', 'trace'].forEach((fn) => {
    try { console[fn] = () => { }; } catch (e) { /* ignore */ }
  });
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Preserve current path so we can redirect back after successful login
  const next = encodeURIComponent(`${location.pathname}${location.search || ''}`);
  return user ? children : <Navigate to={`/login?next=${next}`} replace />;
};

function App() {
  const [hideFooter, setHideFooter] = useState(false);

  // Auth-Routen, auf denen wir SW konsequent deaktivieren (inkl. Unregister + Cache-Clear)
  const AuthSWGuard = () => {
    const location = useLocation();

    useEffect(() => {
      const pathname = location?.pathname || '';
      if (!shouldBypassServiceWorkerForPath(pathname)) return;

      // Best-effort cleanup: verhindern, dass ein bereits aktiver SW den OAuth Redirect stört
      hardDisableServiceWorker();

      // Zusätzlich verhindern: falls ein Update-Event reinkommt, soll es auf Auth-Seiten
      // keinen Auto-Reload triggern, der OAuth-Flows unterbrechen kann.
      try { sessionStorage.setItem('sw:disableAutoReload', '1'); } catch (_) { }

      return () => {
        // Beim Verlassen der Auth-Seiten wieder erlauben
        try { sessionStorage.removeItem('sw:disableAutoReload'); } catch (_) { }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.pathname]);

    return null;
  };

  // Register Service Worker on mount and listen for service worker updates
  useEffect(() => {
    // Register the service worker in production to enable update notifications and SW control
    // (registerServiceWorker() prüft intern ebenfalls Auth-Routen und skippt dort)
    registerServiceWorker();

    // Silent auto-update:
    // - activate waiting SW immediately (skipWaiting)
    // - reload once when the new SW takes control
    const onSWUpdate = (event) => {
      try {
        if (!('serviceWorker' in navigator)) return;

        // prevent reload loops in case multiple update events fire
        const alreadyReloaded = (() => {
          try { return sessionStorage.getItem('sw:autoReloaded') === '1'; } catch (_) { return false; }
        })();
        if (alreadyReloaded) return;

        // Wenn wir explizit auf Auth-Seiten sind, verhindern wir Auto-Reload.
        // (Ein Reload kann OAuth Redirects/Callbacks unterbrechen.)
        try {
          if (sessionStorage.getItem('sw:disableAutoReload') === '1') return;
        } catch (_) { }

        const registration = event?.detail?.registration;

        const reloadOnce = () => {
          try { sessionStorage.setItem('sw:autoReloaded', '1'); } catch (_) { }
          window.location.reload();
        };

        const onControllerChange = () => {
          try { navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange); } catch (_) { }
          reloadOnce();
        };

        try {
          navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        } catch (_) { }

        if (registration && registration.waiting && registration.waiting.state === 'installed') {
          try {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          } catch (_) {
            // if postMessage fails, fallback to reload
            reloadOnce();
          }
          // if controllerchange doesn't fire quickly, fallback
          setTimeout(() => {
            try {
              const stillNotReloaded = sessionStorage.getItem('sw:autoReloaded') !== '1';
              if (stillNotReloaded) reloadOnce();
            } catch (_) {
              reloadOnce();
            }
          }, 2500);
          return;
        }

        // no waiting worker => just reload
        reloadOnce();
      } catch (_) {
        try { window.location.reload(); } catch (e) { /* ignore */ }
      }
    };

    window.addEventListener('sw:update', onSWUpdate);
    return () => {
      window.removeEventListener('sw:update', onSWUpdate);
    };
  }, []);

  // Footer-Sichtbarkeit global per Event von Home.js steuern
  useEffect(() => {
    const onToggle = (e) => {
      try { setHideFooter(!!e?.detail?.hidden); } catch (_) { }
    };
    window.addEventListener('footer:toggle', onToggle);
    return () => window.removeEventListener('footer:toggle', onToggle);
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <OnlineStatusProvider>
            <NotificationsProvider>
              <AuthModalProvider>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <AuthSWGuard />
                  <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100">
                    <ScrollToTop />
                    <AudioUnlocker />
                    <CallRoot>
                      <Navbar />
                    </CallRoot>
                    <main className="bg-white">
                      <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/katalog" element={<Catalog />} />
                        <Route path="/feed" element={<Feed />} />
                        <Route path="/category/:slug" element={<CategoryLanding />} />
                        <Route path="/tags" element={<TagsLanding />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-email" element={<EmailVerification />} />
                        <Route path="/verify-email/success" element={<EmailVerificationSuccess />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/search" element={<Search />} />
                        <Route path="/tag/:tag" element={<TagSearch />} />
                        {/* Legacy URL -> redirect to new category-based URL */}
                        <Route path="/user/:id" element={<LegacyUserRedirect />} />

                        {/* Events */}
                        <Route path="/events" element={<Events />} />
                        <Route path="/events/:id" element={<EventDetail />} />
                        <Route path="/posts/:slug" element={<PostDetail />} />

                        {/* New category-based profile URLs */}
                        <Route path="/:mainSlug/:username" element={<Profile />} />
                        <Route path="/:mainSlug/:subSlug/:username" element={<Profile />} />
                        <Route path="/:mainSlug/:subSlug/:subSubSlug/:username" element={<Profile />} />

                        {/* Protected Routes */}
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <Profile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <Settings />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/account"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <Account />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/my-events"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <MyEvents />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/my-posts"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <MyPosts />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <Notifications />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/messages"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <Messages />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute>
                              <Suspense
                                fallback={
                                  <div className="min-h-screen flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                  </div>
                                }
                              >
                                <AdminDashboard />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />

                        {/* Static Pages */}
                        <Route path="/pages/:slug" element={<StaticPage />} />

                        {/* Tariffs */}
                        <Route
                          path="/pages/tarif"
                          element={
                            <Suspense
                              fallback={
                                <div className="min-h-screen flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                                </div>
                              }
                            >
                              <Tariffs />
                            </Suspense>
                          }
                        />

                        {/* Help Center */}
                        <Route path="/help" element={<HelpHome />} />
                        <Route path="/help/:section" element={<HelpSection />} />
                        <Route path="/help/:section/:slug" element={<HelpPage />} />

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>

                    {/* Footer */}
                    {!hideFooter && <Footer />}

                    {/* Toast Notifications */}
                    <Toaster
                      position="bottom-right"
                      // Container should not capture pointer events for the whole page;
                      // make container allow pointer-events to pass through, and enable
                      // pointerEvents on individual toasts via toastOptions.style below.
                      containerStyle={{ zIndex: 2147483647, overflow: 'visible', pointerEvents: 'none' }}
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#fff',
                          color: '#374151',
                          boxShadow: '0 4px 25px -5px rgba(0, 0, 0, 0.1)',
                          borderRadius: '12px',
                          border: '1px solid #e5e7eb',
                          zIndex: 2147483647,
                          overflow: 'visible',
                          // enable pointer events for the toast itself so buttons are clickable
                          pointerEvents: 'auto'
                        },
                        success: {
                          iconTheme: {
                            primary: '#10b981',
                            secondary: '#fff',
                          },
                        },
                        error: {
                          iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                          },
                        },
                      }}
                    />

                    {/* Cookie Banner */}
                    <CookieBanner />
                  </div>
                </BrowserRouter>
              </AuthModalProvider>
            </NotificationsProvider>
          </OnlineStatusProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
